import { useEffect, useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { 
  DataGridPro, 
  useGridApiRef, 
  GRID_TREE_DATA_GROUPING_FIELD 
} from '@mui/x-data-grid-pro';
import RefreshIcon from '@mui/icons-material/Refresh';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { usePanelStyles } from '@/styles/utilities/hooks';
import { useUsersData } from '@/shared/hooks/useUsersData';
import { useZipfileColumns } from '../hooks/useZipfileColumns';
import RenameDialog from './RenameDialog';
import { zipfileService } from '../services/zipfileService';

export default function ZipfilesGrid({
  zipfiles,
  loading,
  error,
  selectedZipfile,
  onZipfileSelect,
  onRefresh,
  totalCount,
  showLeftSidebar,
  onToggleLeftSidebar,
  treeData = [],  // Tree-structured data when available
  displayCount = 0  // Actual count of displayed rows after filtering
}) {
  const theme = useTheme();
  const panelStyles = usePanelStyles();
  const { fetchUsers } = useUsersData();
  const apiRef = useGridApiRef();

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [zipfileToRename, setZipfileToRename] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Handle edit action
  const handleEdit = useCallback((zipfile) => {
    setZipfileToRename(zipfile);
    setRenameDialogOpen(true);
  }, []);

  // Handle rename save
  const handleRenameSave = useCallback(async (zipfileId, newName) => {
    setRenameLoading(true);
    try {
      // Call the update API
      await zipfileService.updateBulk([{ id_: zipfileId, name: newName }]);

      // Close dialog
      setRenameDialogOpen(false);
      setZipfileToRename(null);

      // Show success message
      setSnackbar({
        open: true,
        message: 'Archive renamed successfully',
        severity: 'success'
      });

      // Refresh the grid to show the new name
      onRefresh();
    } catch (error) {
      console.error('Failed to rename zipfile:', error);
      setSnackbar({
        open: true,
        message: `Failed to rename archive: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setRenameLoading(false);
    }
  }, [onRefresh]);

  // Stable function reference for tree data path
  const getTreeDataPath = useCallback((row) => row.path, []);

  // Use column configuration hook with edit handler
  const { columns, columnVisibilityModel, setColumnVisibilityModel } = useZipfileColumns(handleEdit);
  
  // State for managing tree expansion - initialize with all parent rows collapsed
  const [groupingExpansion, setGroupingExpansion] = useState(() => {
    const initialState = {};
    // Start with all nodes collapsed (empty object means use default collapsed state)
    return initialState;
  });
  
  // Determine if we're using tree data - memoized to prevent recalculation
  const displayData = useMemo(() => {
    const data = treeData.length > 0 ? treeData : zipfiles;
    return data;
  }, [treeData, zipfiles]);
  
  const useTreeData = treeData.length > 0;
  
  // Fetch users data on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{ 
        ...panelStyles.header,
        bgcolor: 'grey.50',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
          {showLeftSidebar && (
            <IconButton 
              size="small" 
              onClick={onToggleLeftSidebar}
              sx={panelStyles.headerIconButton}
            >
              <MenuOpenIcon sx={{ fontSize: theme.dimensions.icon.small }} />
            </IconButton>
          )}
          <Typography sx={panelStyles.headerTitle}>
            FMO Input File Archives ({displayCount || 0})
          </Typography>
        </Box>
        
        <IconButton 
          size="small" 
          onClick={onRefresh}
          disabled={loading}
          sx={panelStyles.headerIconButton}
        >
          <RefreshIcon sx={{ fontSize: theme.dimensions.icon.small }} />
        </IconButton>
      </Box>
      
      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: theme.spacing(2) }}>
            {error}
          </Alert>
        )}
        
        {/* Working DataGridPro without groupingColDef - adding selection model */}
        <DataGridPro
          apiRef={apiRef}
          rows={displayData}
          columns={columns}
          getRowId={(row) => String(row.id_)}
          loading={loading}
          rowHeight={theme.dimensions.dataGrid.rowHeight}
          columnHeaderHeight={theme.dimensions.dataGrid.headerHeight}
          hideFooter
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          treeData={useTreeData}
          getTreeDataPath={useTreeData ? getTreeDataPath : undefined}
          groupingColDef={useTreeData ? {
            width: parseInt(theme.spacing(5)),
            minWidth: parseInt(theme.spacing(5)),
            maxWidth: parseInt(theme.spacing(5)),
            headerName: '',
          } : undefined}
          // Controlled expansion state to prevent resets
          groupingExpansion={groupingExpansion}
          onGroupingExpansionChange={(newModel) => {
            setGroupingExpansion(newModel);
          }}
          // Disable automatic parent-child selection
          rowSelectionPropagation={{ descendants: false, parents: false }}
          // Simple single-row selection model
          rowSelectionModel={selectedZipfile ? { ids: new Set([String(selectedZipfile.id_)]), type: 'include' } : { ids: new Set(), type: 'include' }}
          onRowSelectionModelChange={(model) => {
            console.log('=== Selection Model Change ===');
            console.log('Incoming model:', model);
            console.log('Model IDs:', Array.from(model.ids));
            
            // Get the clicked row ID (should be only one with propagation disabled)
            const clickedId = Array.from(model.ids)[0];
            
            // Check if this is a deselection (clicking on already selected row)
            if (clickedId && selectedZipfile && String(selectedZipfile.id_) === clickedId) {
              console.log('Deselecting - clicked on already selected row');
              onZipfileSelect(null);
              return;
            }
            
            // If no selection, clear
            if (!clickedId) {
              console.log('No selection - clearing');
              onZipfileSelect(null);
              return;
            }
            
            // Find and select the clicked row
            const clickedRow = displayData.find(r => String(r.id_) === clickedId);
            console.log('Clicked row:', clickedRow);
            if (clickedRow) {
              console.log('Row id_:', clickedRow.id_);
              console.log('Row isParentRow:', clickedRow.isParentRow);
              console.log('Row isSpecRow:', clickedRow.isSpecRow);
              onZipfileSelect(clickedRow);
            }
          }}
          disableMultipleRowSelection={true}
          sx={{
            border: 'none',
            height: '100%',
            '& .MuiDataGrid-root': {
              fontSize: theme.typography.fontSize.sm,
            },
            // Customize the tree group column
            '& .MuiDataGrid-treeDataGroupingCell': {
              // Hide the text after the toggle button
              '& .MuiDataGrid-treeDataGroupingCellToggle + span': {
                display: 'none !important'  // Hide the ID text
              }
            },
            '& .MuiDataGrid-cell': {
              fontSize: theme.typography.fontSize.sm,
              padding: theme.spacing(0, 1),
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
              minHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`,
              maxHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`
            },
            '& .MuiDataGrid-columnHeader': {
              fontSize: theme.typography.fontSize.sm,
              padding: theme.spacing(0, 1),
              minHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`,
              maxHeight: `${theme.dimensions.dataGrid.headerHeight}px !important`,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
            },
            '& .MuiDataGrid-row': {
              minHeight: `${theme.dimensions.dataGrid.rowHeight}px !important`,
              maxHeight: `${theme.dimensions.dataGrid.rowHeight}px !important`,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            },
            '& .MuiDataGrid-cell--textLeft': {
              justifyContent: 'flex-start',
            },
            '& .MuiDataGrid-cell--textRight': {
              justifyContent: 'flex-end',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              paddingTop: '0 !important'
            }
          }}
          slots={{
            loadingOverlay: () => (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                height: '100%'
              }}>
                <CircularProgress size={theme.spacing(5)} />
              </Box>
            )
          }}
        />
      </Box>

      {/* Rename Dialog */}
      <RenameDialog
        open={renameDialogOpen}
        onClose={() => {
          setRenameDialogOpen(false);
          setZipfileToRename(null);
        }}
        zipfile={zipfileToRename}
        onSave={handleRenameSave}
        loading={renameLoading}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}